/**
 * Coupon campaign form — create, edit and duplicate.
 *
 * Every field the console supports lives here, with inline validation and a
 * live price preview. The form derives the engine's `discount_type` /
 * `discount_value` from whichever pricing mode the admin chose, so what the
 * preview shows is exactly what checkout will charge.
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Info, Lock, Sparkles, Wand2 } from "lucide-react";
import {
  BATCH_SIZE_OPTIONS,
  BENEFIT_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  MEMBERSHIP_OPTIONS,
  PRICING_MODE_OPTIONS,
  REGION_OPTIONS,
  STATUS_OPTIONS,
  TAG_SUGGESTIONS,
  TIMEZONE_OPTIONS,
  canonicalMrp,
  currencyForRegions,
  emptyCouponForm,
  formatMoney,
  generateCodes,
  previewInputFrom,
  previewPrice,
  validateCouponForm,
  type CouponFormValues,
  type FieldErrors,
} from "@/lib/couponConsole";

interface CouponFormProps {
  value?: CouponFormValues;
  /** Codes already in use, so duplicates are caught before saving. */
  existingCodes: string[];
  institutions: Array<{ id: string; name: string }>;
  partners: Array<{ id: string; name: string }>;
  isSuperAdmin: boolean;
  mode: "create" | "edit" | "duplicate";
  saving?: boolean;
  onCancel: () => void;
  onSubmit: (values: CouponFormValues, codes: string[], reason: string) => void;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <p className="text-xs text-destructive flex items-center gap-1.5 mt-1.5">
      <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" />
      {message}
    </p>
  ) : null;

const CouponForm = ({
  value,
  existingCodes,
  institutions,
  partners,
  isSuperAdmin,
  mode,
  saving,
  onCancel,
  onSubmit,
}: CouponFormProps) => {
  const [values, setValues] = useState<CouponFormValues>(value ?? emptyCouponForm());
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [generated, setGenerated] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");

  const set = <K extends keyof CouponFormValues>(key: K, next: CouponFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: next }));

  /** Keep currency and MRP honest when the region selection changes. */
  useEffect(() => {
    const currency = currencyForRegions(values.regions);
    const mrp = canonicalMrp(values.regions[0] ?? "IN");
    setValues((prev) =>
      prev.currency === currency && prev.mrp === String(mrp.amount)
        ? prev
        : { ...prev, currency, mrp: String(mrp.amount) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.regions.join(",")]);

  const errors: FieldErrors = useMemo(
    () => validateCouponForm(values, { existingCodes, isSuperAdmin }),
    [values, existingCodes, isSuperAdmin],
  );
  const preview = useMemo(() => previewPrice(previewInputFrom(values)), [values]);
  const hasErrors = Object.keys(errors).length > 0;
  const show = (key: keyof FieldErrors) => (touched ? errors[key] : undefined);

  const toggleRegion = (region: string) =>
    set(
      "regions",
      values.regions.includes(region)
        ? values.regions.filter((r) => r !== region)
        : [...values.regions, region],
    );

  const toggleIn = (key: "eligible_institutions" | "eligible_partners", id: string) =>
    set(key, values[key].includes(id) ? values[key].filter((x) => x !== id) : [...values[key], id]);

  const addTag = (tag: string) => {
    const clean = tag.trim();
    if (!clean || values.tags.includes(clean)) return;
    set("tags", [...values.tags, clean]);
    setTagDraft("");
  };

  const handleGenerate = () => {
    const codes = generateCodes({
      count: values.batch_size,
      format: values.code_format,
      prefix: values.code_prefix,
      taken: existingCodes,
    });
    setGenerated(codes);
  };

  const submit = () => {
    setTouched(true);
    if (hasErrors) return;
    const codes = values.auto_generate
      ? generated.length > 0
        ? generated
        : generateCodes({
            count: values.batch_size,
            format: values.code_format,
            prefix: values.code_prefix,
            taken: existingCodes,
          })
      : [values.code.trim().toUpperCase()];
    onSubmit(values, codes, reason);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="basics">
        <TabsList className="w-full grid grid-cols-4 h-auto">
          <TabsTrigger value="basics" className="min-h-11">Basics</TabsTrigger>
          <TabsTrigger value="pricing" className="min-h-11">Pricing</TabsTrigger>
          <TabsTrigger value="limits" className="min-h-11">Limits</TabsTrigger>
          <TabsTrigger value="advanced" className="min-h-11">Advanced</TabsTrigger>
        </TabsList>

        {/* ---------------------------------------------------------- basics */}
        <TabsContent value="basics" className="space-y-5 pt-5">
          <div className="space-y-2">
            <Label htmlFor="c-label">Campaign name</Label>
            <Input
              id="c-label"
              value={values.label}
              onChange={(e) => set("label", e.target.value)}
              placeholder="Launch offer"
              aria-invalid={!!show("label")}
            />
            <FieldError message={show("label")} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-desc">Description</Label>
            <Textarea
              id="c-desc"
              value={values.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What this offer is for, and who it is meant to reach."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-type">Benefit type</Label>
            <Select
              value={values.benefit_type}
              onValueChange={(v) => set("benefit_type", v as CouponFormValues["benefit_type"])}
            >
              <SelectTrigger id="c-type" className="min-h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BENEFIT_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.label} value={o.value}>
                    {o.label} — <span className="text-muted-foreground">{o.hint}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="c-auto" className="cursor-pointer">Generate codes for me</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Create a batch of unique codes instead of one code you type.
              </p>
            </div>
            <Switch
              id="c-auto"
              checked={values.auto_generate}
              onCheckedChange={(v) => set("auto_generate", v)}
              disabled={mode === "edit"}
            />
          </div>

          {values.auto_generate ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="c-format">Format</Label>
                <Select
                  value={values.code_format}
                  onValueChange={(v) => set("code_format", v as "random" | "prefix")}
                >
                  <SelectTrigger id="c-format" className="min-h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    <SelectItem value="prefix">Prefix + random</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-prefix">Prefix</Label>
                <Input
                  id="c-prefix"
                  value={values.code_prefix}
                  onChange={(e) => set("code_prefix", e.target.value.toUpperCase())}
                  disabled={values.code_format !== "prefix"}
                  aria-invalid={!!show("code_prefix")}
                />
                <FieldError message={show("code_prefix")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-batch">How many</Label>
                <Select
                  value={String(values.batch_size)}
                  onValueChange={(v) => set("batch_size", Number(v))}
                >
                  <SelectTrigger id="c-batch" className="min-h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BATCH_SIZE_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n} codes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3">
                <Button type="button" variant="secondary" onClick={handleGenerate} className="min-h-11">
                  <Wand2 className="w-4 h-4 mr-2" aria-hidden="true" />
                  Preview codes
                </Button>
                {generated.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2 font-mono break-all">
                    {generated.slice(0, 8).join(", ")}
                    {generated.length > 8 ? ` … +${generated.length - 8} more` : ""}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="c-code">Coupon code</Label>
              <Input
                id="c-code"
                value={values.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                placeholder="INDEPENDENCE"
                className="font-mono"
                disabled={mode === "edit"}
                aria-invalid={!!show("code")}
              />
              <FieldError message={show("code")} />
              {mode === "edit" && (
                <p className="text-xs text-muted-foreground">
                  A live code cannot be renamed — duplicate it instead.
                </p>
              )}
            </div>
          )}
        </TabsContent>

        {/* --------------------------------------------------------- pricing */}
        <TabsContent value="pricing" className="space-y-5 pt-5">
          <div className="space-y-2">
            <Label>Regions</Label>
            <div className="flex flex-wrap gap-2">
              {REGION_OPTIONS.map((r) => {
                const on = values.regions.includes(r.value);
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => toggleRegion(r.value)}
                    aria-pressed={on}
                    className={`min-h-11 px-4 rounded-2xl border text-sm transition-colors ${
                      on
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card border-border hover:bg-muted"
                    }`}
                  >
                    {r.label}
                    <span className="opacity-70 ml-2">{r.currency}</span>
                  </button>
                );
              })}
            </div>
            <FieldError message={show("regions")} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="c-currency">Currency</Label>
              <Select value={values.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger id="c-currency" className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={show("currency")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-mrp" className="flex items-center gap-2">
                MRP
                <Lock className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
              </Label>
              <Input id="c-mrp" value={values.mrp} readOnly className="bg-muted/50" />
              <p className="text-xs text-muted-foreground">
                The canonical price for this region. Only a super admin can change it.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-mode">How the discount works</Label>
            <Select
              value={values.pricing_mode}
              onValueChange={(v) => set("pricing_mode", v as CouponFormValues["pricing_mode"])}
            >
              <SelectTrigger id="c-mode" className="min-h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRICING_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {values.pricing_mode === "percentage" && (
            <div className="space-y-2">
              <Label htmlFor="c-pct">Percentage off</Label>
              <Input
                id="c-pct"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                value={values.percentage}
                onChange={(e) => set("percentage", e.target.value)}
                aria-invalid={!!show("percentage")}
              />
              <FieldError message={show("percentage")} />
            </div>
          )}
          {values.pricing_mode === "fixed_amount" && (
            <div className="space-y-2">
              <Label htmlFor="c-flat">Amount off ({values.currency})</Label>
              <Input
                id="c-flat"
                type="number"
                inputMode="decimal"
                min={0}
                value={values.fixed_amount}
                onChange={(e) => set("fixed_amount", e.target.value)}
                aria-invalid={!!show("fixed_amount")}
              />
              <FieldError message={show("fixed_amount")} />
            </div>
          )}
          {values.pricing_mode === "final_price" && (
            <div className="space-y-2">
              <Label htmlFor="c-final">Selling price ({values.currency})</Label>
              <Input
                id="c-final"
                type="number"
                inputMode="decimal"
                min={0}
                value={values.final_price}
                onChange={(e) => set("final_price", e.target.value)}
                aria-invalid={!!show("final_price")}
              />
              <FieldError message={show("final_price")} />
            </div>
          )}

          {/* Live preview */}
          <Card className="rounded-3xl border-primary/30 bg-primary/5">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
                What the buyer will see
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground">MRP</p>
                  <p className="text-lg font-semibold line-through opacity-70">
                    {formatMoney(preview.mrp, preview.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">They save</p>
                  <p className="text-lg font-semibold text-primary">
                    {formatMoney(preview.savings, preview.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Percentage off</p>
                  <p className="text-lg font-semibold">{preview.percentOff}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">They pay</p>
                  <p className="text-2xl font-bold">
                    {formatMoney(preview.finalPrice, preview.currency)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Stored for the engine as{" "}
                <span className="font-mono">
                  {preview.discountType} / {preview.discountValue}
                </span>
                .
              </p>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="c-from">Starts</Label>
              <Input
                id="c-from"
                type="datetime-local"
                value={values.valid_from}
                onChange={(e) => set("valid_from", e.target.value)}
                aria-invalid={!!show("valid_from")}
              />
              <FieldError message={show("valid_from")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-until">Ends</Label>
              <Input
                id="c-until"
                type="datetime-local"
                value={values.valid_until}
                onChange={(e) => set("valid_until", e.target.value)}
                aria-invalid={!!show("valid_until")}
              />
              <FieldError message={show("valid_until")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-tz">Time zone</Label>
              <Select value={values.timezone} onValueChange={(v) => set("timezone", v)}>
                <SelectTrigger id="c-tz" className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-status">Status</Label>
            <Select
              value={values.status}
              onValueChange={(v) => set("status", v as CouponFormValues["status"])}
            >
              <SelectTrigger id="c-status" className="min-h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label} — <span className="text-muted-foreground">{s.hint}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only an active campaign can be redeemed at checkout.
            </p>
          </div>
        </TabsContent>

        {/* ---------------------------------------------------------- limits */}
        <TabsContent value="limits" className="space-y-5 pt-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {(
              [
                ["max_redemptions", "Total uses", "Leave empty for unlimited"],
                ["max_uses_per_user", "Per person", "Usually 1"],
                ["max_uses_per_email", "Per email", "Usually 1"],
                ["max_uses_per_institution", "Per institution", "Leave empty for unlimited"],
                ["max_uses_per_partner", "Per partner", "Leave empty for unlimited"],
                ["min_purchase_amount", "Minimum purchase", "Leave empty for none"],
              ] as Array<[keyof CouponFormValues, string, string]>
            ).map(([key, label, hint]) => (
              <div key={String(key)} className="space-y-2">
                <Label htmlFor={`c-${String(key)}`}>{label}</Label>
                <Input
                  id={`c-${String(key)}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={values[key] as string}
                  onChange={(e) => set(key, e.target.value as never)}
                  placeholder={hint}
                  aria-invalid={!!show(key as keyof FieldErrors)}
                />
                <FieldError message={show(key as keyof FieldErrors)} />
              </div>
            ))}
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="c-member">Who can use it</Label>
            <Select
              value={values.eligible_membership}
              onValueChange={(v) => set("eligible_membership", v)}
            >
              <SelectTrigger id="c-member" className="min-h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEMBERSHIP_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {institutions.length > 0 && (
            <div className="space-y-2">
              <Label>Limit to institutions</Label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {institutions.map((i) => {
                  const on = values.eligible_institutions.includes(i.id);
                  return (
                    <button
                      key={i.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleIn("eligible_institutions", i.id)}
                      className={`min-h-11 px-4 rounded-2xl border text-sm ${
                        on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                      }`}
                    >
                      {i.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Leave all unselected to allow every institution.
              </p>
            </div>
          )}

          {partners.length > 0 && (
            <div className="space-y-2">
              <Label>Limit to partners</Label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                {partners.map((p) => {
                  const on = values.eligible_partners.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleIn("eligible_partners", p.id)}
                      className={`min-h-11 px-4 rounded-2xl border text-sm ${
                        on ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        {/* -------------------------------------------------------- advanced */}
        <TabsContent value="advanced" className="space-y-5 pt-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="c-stack" className="cursor-pointer">Stackable</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Can be combined with another benefit. Off by default.
              </p>
            </div>
            <Switch
              id="c-stack"
              checked={values.stackable}
              onCheckedChange={(v) => set("stackable", v)}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="c-internal" className="cursor-pointer">Internal only</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Hidden from every public list and never advertised.
              </p>
            </div>
            <Switch
              id="c-internal"
              checked={values.internal_only}
              onCheckedChange={(v) => set("internal_only", v)}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="c-approval" className="cursor-pointer">Needs approval</Label>
              <p className="text-xs text-muted-foreground mt-1">
                An admin must approve each request before it can be redeemed.
              </p>
            </div>
            <Switch
              id="c-approval"
              checked={values.requires_approval}
              onCheckedChange={(v) => set("requires_approval", v)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-priority">Priority</Label>
            <Input
              id="c-priority"
              type="number"
              inputMode="numeric"
              min={0}
              value={values.priority}
              onChange={(e) => set("priority", e.target.value)}
              aria-invalid={!!show("priority")}
            />
            <p className="text-xs text-muted-foreground">
              Lower wins when two benefits could both apply.
            </p>
            <FieldError message={show("priority")} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-partnerorg">Partner or institution name</Label>
            <Input
              id="c-partnerorg"
              value={values.partner_org}
              onChange={(e) => set("partner_org", e.target.value)}
              placeholder="Optional label shown in reports"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-tags">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="c-tags"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag(tagDraft);
                  }
                }}
                placeholder="Add a tag and press Enter"
              />
              <Button type="button" variant="secondary" className="min-h-11" onClick={() => addTag(tagDraft)}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {values.tags.map((t) => (
                <Badge
                  key={t}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => set("tags", values.tags.filter((x) => x !== t))}
                >
                  {t} ×
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {TAG_SUGGESTIONS.filter((t) => !values.tags.includes(t)).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => addTag(t)}
                  className="text-xs px-3 py-2 rounded-full border border-dashed border-border text-muted-foreground hover:bg-muted min-h-9"
                >
                  + {t}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="c-notes">Internal notes</Label>
            <Textarea
              id="c-notes"
              value={values.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
            />
          </div>
        </TabsContent>
      </Tabs>

      <Separator />

      <div className="space-y-2">
        <Label htmlFor="c-reason">Reason for this change</Label>
        <Input
          id="c-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Recorded in the audit log"
        />
      </div>

      {touched && errors.form && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          {errors.form}
        </div>
      )}

      {!isSuperAdmin && (
        <p className="text-xs text-muted-foreground flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          A 100% free coupon and any change to the canonical MRP need a super admin.
        </p>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <Button variant="outline" className="min-h-12" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button className="min-h-12" onClick={submit} disabled={saving}>
          {saving
            ? "Saving…"
            : mode === "edit"
              ? "Save changes"
              : values.auto_generate
                ? `Create ${values.batch_size} codes`
                : "Create campaign"}
        </Button>
      </div>
    </div>
  );
};

export default CouponForm;
